import { useState } from 'react'
import Button from '../../../shared/components/Button'
import { useAttributeSchemas, useDeleteAttributeSchema } from '../hooks/useAttributeSchemas'
import { AttributeSchemaModal } from '../modals/AttributeSchemaModal'
import { useAuthStore } from '../../auth/store'
import type { AttributeSchema } from '../types'

interface ProductAttributeSchemaPanelProps {
  /** The solution these attribute schemas belong to — set by the page's Solution selector. */
  solutionId: string
}

const DATA_TYPE_BADGE: Record<string, string> = {
  ENUM: 'bg-blue-50 text-blue-700',
  COLOR: 'bg-purple-50 text-purple-700',
  STRING: 'bg-muted text-muted-foreground',
  NUMBER: 'bg-amber-50 text-amber-700',
}

/**
 * TCS-admin authoring surface for PRODUCT attribute schemas — the dynamic attributes
 * merchants fill in when creating products (size, colour, material, ...). Sits alongside
 * the Booking / Custom Order / Package tabs in the Schema Builder page so TCS admins
 * build every domain's schema (products + intake fields) from one place.
 *
 * Backend: productsApi.platform (attribute-schemas via osprey-api → product-service).
 * A schema with `tenantId === null` is solution-wide (the default for every merchant on
 * this solution); a schema with `tenantId` set is a merchant-specific override.
 */
export function ProductAttributeSchemaPanel({ solutionId }: ProductAttributeSchemaPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSchema, setEditingSchema] = useState<AttributeSchema | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const selectedTenantId = useAuthStore((state) => state.selectedTenantId)
  const { data: schemas = [], isLoading, error } = useAttributeSchemas(solutionId, selectedTenantId || undefined)
  const deleteMutation = useDeleteAttributeSchema()

  const solutionSchemas = schemas.filter((s) => !s.tenantId)
  const merchantSchemas = schemas.filter((s) => !!s.tenantId)

  function handleEdit(schema: AttributeSchema) {
    setEditingSchema(schema)
    setIsModalOpen(true)
  }

  function handleNew() {
    setEditingSchema(null)
    setIsModalOpen(true)
  }

  function handleModalClose() {
    setIsModalOpen(false)
    setEditingSchema(null)
  }

  async function handleDelete(schema: AttributeSchema) {
    if (!window.confirm(`Delete attribute schema "${schema.label}" (${schema.key})? This cannot be undone.`)) return
    setDeletingId(schema.id)
    try {
      await deleteMutation.mutateAsync({ id: schema.id, solutionId: schema.solutionId })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground max-w-2xl">
          These are the dynamic attributes (size, colour, material, …) merchants fill in when
          creating products for <strong>{solutionId}</strong>. Solution-wide schemas apply to every
          merchant; merchant-specific overrides apply only to the merchant they name.
        </p>
        <Button size="sm" onClick={handleNew} className="shrink-0">
          + New Schema
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground py-4">Loading schemas…</p>}

      {!isLoading && error && (
        <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-center">
          <p className="text-sm font-medium text-red-700">Failed to load attribute schemas</p>
          <p className="text-xs text-red-500 mt-1">Check that the upstream service is running and reachable.</p>
        </div>
      )}

      {!isLoading && !error && schemas.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-10 text-center text-muted-foreground">
          <p className="text-sm">No attribute schemas for <strong>{solutionId}</strong>.</p>
          <p className="text-xs mt-1">Create one to enable dynamic product fields for merchants.</p>
        </div>
      )}

      {solutionSchemas.length > 0 && (
        <SchemaTable
          title={`${solutionId} — solution-wide`}
          schemas={solutionSchemas}
          deletingId={deletingId}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {merchantSchemas.length > 0 && (
        <SchemaTable
          title="Merchant-specific overrides"
          schemas={merchantSchemas}
          deletingId={deletingId}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <AttributeSchemaModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        schema={editingSchema}
        defaultSolutionId={solutionId}
      />
    </div>
  )
}

function SchemaTable({
  title,
  schemas,
  deletingId,
  onEdit,
  onDelete,
}: {
  title: string
  schemas: AttributeSchema[]
  deletingId: string | null
  onEdit: (s: AttributeSchema) => void
  onDelete: (s: AttributeSchema) => void
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{title}</h3>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Key</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Label</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Type</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Allowed Values</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Required</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Order</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {schemas
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((schema) => (
                <tr key={schema.id} className="hover:bg-muted">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{schema.key}</td>
                  <td className="px-4 py-3 text-foreground">{schema.label}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${DATA_TYPE_BADGE[schema.dataType] ?? 'bg-muted text-muted-foreground'}`}>
                      {schema.dataType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                    {schema.allowedValues.length > 0 ? schema.allowedValues.join(', ') : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {schema.isRequired ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-muted-foreground">No</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{schema.sortOrder}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => onEdit(schema)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                      <button
                        onClick={() => onDelete(schema)}
                        disabled={deletingId === schema.id}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        {deletingId === schema.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
